import { DeviceActivityHistory } from './DeviceActivity';
import type { DeviceFacts, DeviceTelemetry, JobView } from '@api/devices/contracts';
import { SCRIPT_MAX_BYTES, scriptBytes } from '@api/devices/contracts';
import { reviewScript } from '@api/operations/script-review';
import { Search, ShieldCheck, SquareTerminal, Terminal, TriangleAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { formatBytes } from '../lib/format';
import { ApprovalDialog, type DeviceRef, JobRow, useCapabilities, useJobActions } from '../operations/jobs';
import { ScriptSessionBar, timeLeft, useScriptSession } from '../operations/ScriptSession';
import { TemplateRunDialog, useScriptTemplates } from '../operations/templates';
import { ReviewList } from '../operations/meta';
import { useScope } from '../scope/scope';
import { useDeviceJobs, useRemoteStatus, useSoftware } from '../search/hooks';
import { emptyFilters, writeFilters } from '../search/state';
import { useDebounced } from '../shell/CommandPalette';
import { Button } from '../ui/Button';
import { FreeSpaceChart, TimeSeriesChart, UsageBar } from '../ui/charts';
import { cn } from '../ui/cn';
import { Card, EmptyState, Skeleton, useNow } from '../ui/misc';
import { UpdatesCard } from './UpdatesCard';
import { describePresence } from './presence';

export function PerformancePanel({ t, range, onRange }: { t: DeviceTelemetry; range: '24h' | '7d'; onRange: (r: '24h' | '7d') => void }) {
  const toggle = (
    <div role="radiogroup" aria-label="Time range" className="inline-flex rounded-lg border border-line bg-panel p-0.5 text-[12.5px]">
      {(['24h', '7d'] as const).map(r => (
        <button key={r} type="button" role="radio" aria-checked={range === r} onClick={() => onRange(r)}
          className={cn('rounded-md px-2.5 py-1 font-medium', range === r ? 'bg-ink text-canvas' : 'text-ink-2 hover:text-ink')}>{r === '24h' ? '24 hours' : '7 days'}</button>
      ))}
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3"><p className="text-[13px] text-ink-3">Averaged every {t.metrics.bucketMinutes} minutes from samples the agent sends each minute.</p>{toggle}</div>
      {(['cpuPct', 'memoryPct'] as const).map(key => (
        <Card key={key} className="p-4">
          <h3 className="mb-2 text-[13px] font-semibold text-ink-2">{key === 'cpuPct' ? 'CPU' : 'Memory'} <span className="font-normal text-ink-3">· now {t.current[key] === null ? '—' : `${Math.round(t.current[key]!)}%`}</span></h3>
          <TimeSeriesChart label={key === 'cpuPct' ? 'CPU use' : 'Memory use'} range={range} points={t.metrics.points.map(p => ({ at: p.at, value: p[key] }))}
            threshold={{ value: 90, label: '90% for 30 min raises a signal' }} />
        </Card>
      ))}
    </div>
  );
}

export function StoragePanel({ t }: { t: DeviceTelemetry }) {
  if (!t.volumes.length) return <Card><EmptyState title="No volumes reported" /></Card>;
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      {t.volumes.map(v => {
        const pct = (100 * v.freeBytes) / v.totalBytes;
        const days = v.projection.daysToFull;
        return (
          <Card key={v.mount} className="p-4">
            <div className="flex items-baseline justify-between gap-2">
              <h3 className="font-mono text-[14px] font-semibold">{v.mount}</h3>
              <span className="text-[12px] text-ink-3">{[v.label, v.filesystem, v.driveType && v.driveType !== 'unknown' ? v.driveType : null, v.system ? 'system' : null, v.encrypted === true ? 'encrypted' : v.encrypted === false ? 'not encrypted' : null].filter(Boolean).join(' · ')}</span>
            </div>
            <p className={cn('mt-1 text-[15px]', pct < 5 ? 'text-offline' : pct < 15 ? 'text-attention' : 'text-ink')}><span className="font-semibold">{formatBytes(v.freeBytes)}</span> free of {formatBytes(v.totalBytes)} ({pct.toFixed(1)}%)</p>
            <div className="mt-2"><UsageBar used={v.totalBytes - v.freeBytes} total={v.totalBytes} tone={pct < 5 ? 'critical' : pct < 15 ? 'warn' : 'normal'} /></div>
            <p className="mt-2 text-[12.5px] text-ink-2">
              {days !== null ? <><TriangleAlert className="mr-1 inline size-3.5 text-attention" />At the current rate ({formatBytes(-(v.projection.bytesPerDay ?? 0))}/day) it fills in about <b>{Math.max(1, Math.round(days))} days</b>.</>
                : v.projection.samples < 6 ? 'Not enough history for a trend yet.' : (v.projection.bytesPerDay ?? 0) < 0 ? 'Slowly changing; no steady trend toward full.' : 'Stable or growing free space.'}
            </p>
            <div className="mt-2"><FreeSpaceChart history={v.history} totalBytes={v.totalBytes} daysToFull={days} /></div>
          </Card>
        );
      })}
    </div>
  );
}

export function SoftwarePanel({ deviceId, facts, collectedAt }: { deviceId: string; facts?: DeviceFacts | null; collectedAt?: string | null }) {
  const { mspId } = useScope();
  const [text, setText] = useState('');
  const q = useDebounced(text.trim(), 200);
  const software = useSoftware(deviceId, q);
  const fleetLink = (name: string) => `/w/${mspId}/devices?${(() => { const p = writeFilters({ ...emptyFilters, kind: 'device' }, { kind: 'device' }); p.set('software', name); return p; })()}`;
  return (
    <div className="space-y-4">
      <UpdatesCard facts={facts} collectedAt={collectedAt} />
      <Card className="overflow-hidden">
        <label className="relative flex items-center border-b border-line">
          <Search className="pointer-events-none absolute left-4 size-4 text-ink-3" />
          <input value={text} onChange={e => setText(e.target.value)} placeholder="Filter by name or publisher" aria-label="Filter software" className="h-11 w-full bg-transparent pl-10 pr-4 outline-none" />
        </label>
        {software.isPending ? <div className="space-y-2 p-4">{[0, 1, 2].map(i => <Skeleton key={i} className="w-2/3" />)}</div>
          : !software.data?.length ? <EmptyState title={q ? 'Nothing matches' : 'No software reported'} />
          : (
            <table className="w-full table-fixed text-[13px]">
              <caption className="sr-only">Installed software</caption>
              <thead><tr className="border-b border-line text-left text-[12px] text-ink-3"><th className="py-2 pl-4 font-medium">Name</th><th className="w-[22%] font-medium">Version</th><th className="hidden w-[26%] font-medium md:table-cell">Publisher</th><th className="w-28" /></tr></thead>
              <tbody>
                {software.data.map(s => (
                  <tr key={`${s.name}@${s.version}`} className="group border-b border-line/70 last:border-0">
                    <td className="truncate py-2 pl-4 font-medium">{s.name}</td>
                    <td className="truncate font-mono text-[12.5px] text-ink-2">{s.version || '—'}</td>
                    <td className="hidden truncate text-ink-3 md:table-cell">{s.publisher ?? '—'}</td>
                    <td className="pr-3 text-right"><Link to={fleetLink(s.name.replace(/\s*\(x64\)|\s+\d[\d.]*$/g, '').slice(0, 60))} className="text-[12px] font-medium text-ink-3 opacity-0 hover:text-ink group-hover:opacity-100 focus:opacity-100">Other devices →</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </Card>
    </div>
  );
}

export function ActivityPanel({ deviceId, onApprove }: { deviceId: string; t: DeviceTelemetry | undefined; onApprove: (job: JobView) => void }) {
  const jobs = useDeviceJobs(deviceId);
  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
      <section className="space-y-2">
        <h3 className="px-1 text-[13px] font-semibold text-ink-2">Jobs</h3>
        <Card className="overflow-hidden">
          {jobs.isPending ? <div className="p-4"><Skeleton className="w-1/2" /></div> : !jobs.data?.length ? <EmptyState title="No jobs yet" />
            : jobs.data.map((j, i) => <JobRow key={j.id} job={j} showDevice={false} defaultOpen={i === 0 && ['running', 'succeeded', 'failed'].includes(j.state)} onApprove={onApprove} />)}
        </Card>
      </section>
      <DeviceActivityHistory key={deviceId} deviceId={deviceId} />
    </div>
  );
}

const SNIPPETS: Array<[string, string]> = [
  ['Signed-in users and sessions', 'quser 2>&1'],
  ['Largest folders in user profiles', `Get-ChildItem C:\\Users -Directory | ForEach-Object {
  $size = (Get-ChildItem $_.FullName -Recurse -File -Force -ErrorAction SilentlyContinue | Measure-Object Length -Sum).Sum
  [pscustomobject]@{ User = $_.Name; SizeGB = [math]::Round($size / 1GB, 1) }
} | Sort-Object SizeGB -Descending | Format-Table -AutoSize`],
  ['Installed updates (last 10)', 'Get-HotFix | Sort-Object InstalledOn -Descending | Select-Object -First 10 HotFixID, InstalledOn'],
  ['Printers and queues', 'Get-Printer | Select-Object Name, DriverName, PortName, PrinterStatus | Format-Table -AutoSize\nGet-PrintJob -PrinterName * -ErrorAction SilentlyContinue'],
  ['Test a connection', "Test-NetConnection -ComputerName 'outlook.office365.com' -Port 443"],
];

/** Who is at the computer, beside the terminal: picking a quiet moment is the technician's call (WRMM-159). */
function AtTheComputer({ presence }: { presence?: DeviceFacts['presence'] }) {
  const summary = describePresence(presence, useNow(30_000));
  if (!summary) return null;
  return <div className="border-b border-line px-4 py-1.5 text-[12.5px] text-ink-2">At the computer: {summary.text}
    {summary.asOf && <span className="text-ink-3"> · as of {summary.asOf}</span>}
    <span className="text-ink-3"> · A terminal doesn’t notify or ask them.</span></div>;
}

export function PowerShellPanel({ device, os, presence, agent }: { device: DeviceRef; os: string | null; presence?: DeviceFacts['presence']; agent?: DeviceFacts['agent'] }) {
  const caps = useCapabilities(device);
  const { create } = useJobActions();
  const jobs = useDeviceJobs(device.id);
  const { mspId } = useScope();
  const navigate = useNavigate();
  // The full-access terminal (WRMM-68) runs through WITTCO Remote: offered where it is set up and this person could open one.
  const remote = useRemoteStatus(caps.remote && caps.scripts && caps.approve ? device.id : undefined).data;
  const linux = os === 'linux', macos = os === 'macos', unix = linux || macos;
  const [macShell, setMacShell] = useState<'zsh' | 'bash'>('zsh');
  const interpreter = macos ? macShell : linux ? 'bash' : 'powershell';
  const shell = interpreter === 'zsh' ? 'Zsh' : interpreter === 'bash' ? 'Bash' : 'PowerShell';
  const snippets: Array<[string, string]> = linux ? [['Services needing attention', 'systemctl --failed --no-pager'], ['Recent system errors', 'journalctl -p err -n 100 --no-pager'], ['Storage and mounts', 'df -hT\nlsblk -f'], ['Network', 'ip -brief address\nip route'], ['User sessions', 'loginctl list-sessions --no-pager']] : macos ? [['System version', 'sw_vers'], ['Storage', 'df -h /'], ['FileVault', 'fdesetup status'], ['Network', 'ifconfig -a']] : SNIPPETS;
  const openTerminal = (mode = 'shell') => {
    const path = `/w/${mspId}/terminal/${device.id}?mode=${mode}`;
    if (!window.open(path, `wittco-terminal-${device.id}`, 'popup=yes,width=1100,height=720')) navigate(path);
  };
  const [script, setScript] = useState('');
  const [runAs, setRunAs] = useState<'system' | 'user'>('system');
  const [timeoutSeconds, setTimeoutSeconds] = useState(300);
  const [reason, setReason] = useState('');
  const [approving, setApproving] = useState<JobView | null>(null);
  // Script library templates (WRMM-230), Windows only: each run is its own job with the template's exact bytes.
  const templates = useScriptTemplates(os === 'windows' && caps.scripts).data?.templates ?? [];
  const [running, setRunning] = useState<string | null>(null);
  // An open PowerShell session (WRMM-82) approves each script as it is requested; its reason stands in for a blank one.
  const session = useScriptSession(device.id);
  const live = os === 'windows' && caps.approve && !!session.data?.live && !!timeLeft(session.data, Date.now());
  const why = reason.trim() || (live ? session.data!.reason : '');
  const typed = useDebounced(script, 250);
  const review = useMemo(() => (typed.trim() ? reviewScript(typed, runAs, interpreter) : []), [typed, runAs, interpreter]);
  const high = review.filter(f => f.severity === 'high').length;
  // The API limits scripts in UTF-8 bytes, so a script of accented or non-Latin text reaches it in fewer characters.
  const size = scriptBytes(script), over = size - SCRIPT_MAX_BYTES, limit = `${SCRIPT_MAX_BYTES / 1024} KB`;
  if (os !== 'windows' && !unix) return <Card><EmptyState icon={<Terminal className="size-5" />} title="Shell unavailable">This operating system does not yet support native shell jobs.</EmptyState></Card>;
  if (!caps.scripts) return <Card><EmptyState icon={<Terminal className="size-5" />} title="You can’t run scripts here">Running scripts is a separate permission from viewing devices. Ask an owner to grant it.</EmptyState></Card>;
  const submit = () => create.mutate({ deviceId: device.id, reason: why, script: { text: script, runAs, timeoutSeconds, ...(macos ? { shell: macShell } : {}) } }, {
    // Approved by the session already, it simply runs; otherwise it waits for a key touch.
    onSuccess: job => { if (caps.approve && job.state === 'awaiting_approval') setApproving(job); setScript(''); setReason(''); },
  });
  const scripts = (jobs.data ?? []).filter(j => j.operation === 'script');
  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
          <Terminal className="size-4 text-ink-3" /><span className="text-[13px] font-semibold">{shell} on <span className="font-mono">{device.title}</span></span>
          <span className="text-[12px] text-ink-3">· {unix ? shell : 'Windows PowerShell 5.1, 64-bit'}</span>
          {macos && <select aria-label="macOS script shell" value={macShell} onChange={e => setMacShell(e.target.value as 'zsh' | 'bash')} className="h-8 rounded-md border border-line bg-panel px-2 text-[12.5px]"><option value="zsh">Zsh</option><option value="bash">Bash</option></select>}
          <span className="flex-1" />
          {!macos && remote && remote.state !== 'not_configured' && (
            <Button size="sm" variant="secondary" onClick={() => openTerminal()} disabled={remote.state !== 'ready'}
              title={remote.state === 'ready' ? 'A live terminal: one key touch for the whole session, recorded' : 'Needs WITTCO Remote connected on this device'}>
              <SquareTerminal className="size-3.5" />Full-access terminal
            </Button>
          )}
          {linux && remote?.state === 'ready' && <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => openTerminal('support')}>Persistent support</Button>
            <Button size="sm" variant="secondary" onClick={() => openTerminal('tmux')}>Attach tmux</Button>
            <Button size="sm" variant="secondary" onClick={() => openTerminal('tmux-view')}>View tmux</Button>
          </div>}
          {os === 'windows' && templates.length > 0 && (
            <select aria-label="Run a template" value="" onChange={e => setRunning(e.target.value || null)}
              className="h-8 rounded-md border border-line bg-panel px-2 text-[12.5px] text-ink-2">
              <option value="">From template…</option>{templates.map(t => <option key={t.id} value={t.id}>{t.name} · v{t.version}</option>)}
            </select>
          )}
          <select aria-label="Insert a snippet" value="" onChange={e => { const s = snippets.find(x => x[0] === e.target.value); if (s) setScript(c => (c ? `${c}\n${s[1]}` : s[1])); }}
            className="h-8 rounded-md border border-line bg-panel px-2 text-[12.5px] text-ink-2">
            <option value="">Insert snippet…</option>{snippets.map(([label]) => <option key={label}>{label}</option>)}
          </select>
        </div>
        {!macos && remote && remote.state !== 'not_configured' && <AtTheComputer presence={presence} />}
        {os === 'windows' && <ScriptSessionBar deviceId={device.id} reason={reason} canApprove={caps.approve} />}
        <textarea value={script} onChange={e => setScript(e.target.value)} spellCheck={false} aria-label={`${shell} script`} rows={10}
          placeholder={macos ? '# The exact text here is what gets approved and run.\nsw_vers' : linux ? '# The exact text here is what gets approved and run.\nsystemctl --failed --no-pager' : '# The exact text here is what gets approved and run.\nGet-Service -Name Spooler'}
          className="block w-full resize-y bg-[#0a1620] p-4 font-mono text-[13px] leading-relaxed text-[#d6e2ea] outline-none placeholder:text-[#5d7384]" />
        {script && (over > 0
          ? <p role="alert" className="border-t border-line px-4 py-2 text-[12.5px] font-medium text-offline">
              Too long to send: {over.toLocaleString()} {over === 1 ? 'byte' : 'bytes'} over the {limit} limit (characters outside plain ASCII take 2–4 bytes each). Shorten the script or split it into separate jobs.</p>
          : <p className="border-t border-line px-4 py-1.5 text-right text-[12px] tabular-nums text-ink-3">{formatBytes(size)} of {limit}</p>)}
        <div className="grid gap-3 border-t border-line p-4 md:grid-cols-[1fr_auto]">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px]">
            <fieldset className="flex items-center gap-3"><legend className="sr-only">Run as</legend>
              {(['system', 'user'] as const).map(r => (
                <label key={r} className="flex items-center gap-1.5"><input type="radio" name="runAs" checked={runAs === r} onChange={() => setRunAs(r)} className="accent-[var(--c-brand)]" />
                  {r === 'system' ? (unix ? 'As root' : 'As SYSTEM') : 'As signed-in user'}</label>
              ))}
            </fieldset>
            <label className="flex items-center gap-1.5 text-ink-2">Stop after
              <select value={timeoutSeconds} onChange={e => setTimeoutSeconds(Number(e.target.value))} className="h-8 rounded-md border border-line bg-panel px-1.5">
                {[[60, '1 min'], [300, '5 min'], [900, '15 min'], [3600, '1 hour']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></label>
            <input value={reason} onChange={e => setReason(e.target.value)} placeholder={live ? 'Why? (the session’s reason if blank)' : 'Why? (recorded with the job)'} aria-label="Reason" maxLength={500}
              className="h-8 min-w-[220px] flex-1 rounded-md border border-line bg-panel px-2.5 outline-none focus:border-brand/50" />
          </div>
          <Button variant="primary" disabled={!script.trim() || !why || over > 0 || create.isPending} onClick={submit}>
            <ShieldCheck className="size-4" />{live ? 'Run' : caps.approve ? 'Review and approve' : 'Request approval'}
          </Button>
        </div>
        {review.length > 0 && (
          <div className="border-t border-line bg-sunken/40 px-4 py-3">
            <p className="mb-2 text-[12.5px] font-semibold text-ink-2">Review notes{high ? <span className="text-offline"> · {high} high</span> : null}<span className="font-normal text-ink-3"> · rule-based, not a security scan</span></p>
            <ReviewList findings={review} compact />
          </div>
        )}
      </Card>
      <p className="text-[12.5px] text-ink-3">Every script needs an approval with a security key, bound to these exact bytes, this device and its run-as account{os === 'windows' && ', or your PowerShell session on this device: one key touch for 15 minutes of your PowerShell scripts here, each still recorded with its exact bytes'}. The device receives a signed job, checks the approval itself and reports what happened. Output appears below and in Work. {!macos && 'For a long hands-on session, the full-access terminal is a live shell: one key touch until you close it, with everything typed and shown recorded.'}</p>
      {scripts.length > 0 && (
        <section className="space-y-2">
          <h3 className="px-1 text-[13px] font-semibold text-ink-2">Recent scripts on this device</h3>
          <Card className="overflow-hidden">{scripts.slice(0, 10).map((j, i) => <JobRow key={j.id} job={j} showDevice={false} defaultOpen={i === 0} onApprove={setApproving} />)}</Card>
        </section>
      )}
      <ApprovalDialog job={approving} open={!!approving} onOpenChange={o => { if (!o) setApproving(null); }} />
      <TemplateRunDialog template={templates.find(t => t.id === running) ?? null} device={device} agent={agent} open={!!running}
        onOpenChange={o => { if (!o) setRunning(null); }} onNeedsApproval={job => { if (caps.approve) setApproving(job); }} />
    </div>
  );
}
